import { Flex } from 'core/components';
import React, { Component } from 'react';
import { Field, FormDialog, InputGroup, PasswordInput, RadioGroup } from 'core/form';
import { inject, observer } from 'mobx-react';
import LabelSelector from 'app/components/labels/LabelSelector';
import { CREDENTIAL_TYPES } from '@kentik/ui-shared/credentials/constants';

const sshCredentialFields = {
  credentialName: {
    label: 'Credential Name',
    rules: 'required|alpha_dash',
    transform: {
      out: (value) => value.trim()
    },
    messages: {
      not_in: 'Name must be unique'
    }
  },

  labels: {
    label: 'Labels',
    defaultValue: []
  },

  type: {
    label: 'Type',
    defaultValue: CREDENTIAL_TYPES.sshpw,
    options: [
      { label: 'SSH Password', value: CREDENTIAL_TYPES.sshpw },
      { label: 'SSH Key', value: CREDENTIAL_TYPES.sshkey }
    ]
  },

  username: {
    label: 'Username',
    rules: 'required',
    messages: {
      required: ':attribute is required.'
    }
  },

  password: {
    label: 'Password',
    rules: [{ required_if: ['type', CREDENTIAL_TYPES.sshpw] }],
    messages: {
      required_if: ':attribute is required.'
    }
  },

  privateKey: {
    label: 'Private Key',
    rules: [{ required_if: ['type', CREDENTIAL_TYPES.sshkey] }],
    messages: {
      required_if: ':attribute is required.'
    }
  },

  passphrase: {
    label: 'Passphrase (optional)'
  }
};

@inject('$credentials')
@observer
class SetupSshCredentials extends Component {
  static defaultProps = {
    types: [CREDENTIAL_TYPES.sshpw, CREDENTIAL_TYPES.sshkey],
    title: 'Add SSH Credential',
    hideTypeSelector: false
  };

  get typeOptions() {
    const { types } = this.props;
    return sshCredentialFields.type.options.filter(({ value }) => types.includes(value));
  }

  handleSubmit = (form) => {
    const { $credentials, model, onClose } = this.props;
    const formValues = form.getValues();
    const { type, credentialName, labels, username, password, privateKey, passphrase } = formValues;
    const tokens = [];

    // Add username token
    tokens.push({
      key: 'username',
      value: username
    });

    // Add type-specific tokens
    if (type === CREDENTIAL_TYPES.sshpw) {
      tokens.push({
        key: 'password',
        value: password
      });
    } else if (type === CREDENTIAL_TYPES.sshkey) {
      tokens.push({
        key: 'private_key',
        value: privateKey
      });

      // Add passphrase if provided
      if (passphrase) {
        tokens.push({
          key: 'passphrase',
          value: passphrase
        });
      }
    }

    return model
      .save({
        name: credentialName,
        labels,
        type,
        tokens
      })
      .then((res) => {
        console.warn('Saved SSH credential', res);
        // refresh the $credentials collection
        $credentials.groupedCollection.fetch({ force: true }).then(() => {
          onClose(formValues);
        });
      });
  };

  render() {
    const { onClose, isOpen, model, hideTypeSelector, title } = this.props;
    const { typeOptions } = this;
    const nameRules = `required|alpha_dash|not_in:${model.collection.map((cred) => cred.get('name'))}`;

    return (
      <FormDialog
        fields={sshCredentialFields}
        options={{ name: 'ssh-credential', showPristineErrors: false }}
        model={model}
        isOpen={isOpen}
        canOutsideClickClose={false}
        entityName="Credential"
        onClose={onClose}
        width={600}
        title={title}
        formActionsProps={{
          onSubmit: this.handleSubmit,
          onCancel: onClose,
          closeAfterSave: false
        }}
      >
        {({ form }) => {
          const isSshKey = form.getValue('type') === CREDENTIAL_TYPES.sshkey;
          const isSshPassword = form.getValue('type') === CREDENTIAL_TYPES.sshpw;

          return (
            <Flex flexDirection="column" gap={3}>
              {!hideTypeSelector && (
                <Field name="type" options={typeOptions} large m={0}>
                  <RadioGroup />
                </Field>
              )}

              <Field name="credentialName" large rules={nameRules} m={0}>
                <InputGroup width={400} />
              </Field>

              <LabelSelector fieldName="labels" width={400} m={0} />

              <Field name="username" large m={0}>
                <InputGroup width={400} />
              </Field>

              {isSshPassword && (
                <Field name="password" large m={0}>
                  <PasswordInput width={400} />
                </Field>
              )}

              {isSshKey && (
                <>
                  <Field name="privateKey" large m={0}>
                    <InputGroup
                      width={400}
                      multiline
                      rows={8}
                      placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;...&#10;-----END RSA PRIVATE KEY-----"
                    />
                  </Field>

                  <Field name="passphrase" large m={0}>
                    <PasswordInput width={400} />
                  </Field>
                </>
              )}
            </Flex>
          );
        }}
      </FormDialog>
    );
  }
}

export default SetupSshCredentials;
